/**
 * API functions for the Wonder Signup components
 * These will be used for submitting signup form data
 */

// API base URL - this will be replaced with the actual endpoint
const API_BASE_URL = '/api';

/**
 * Maps a South African province name to a Location ID.
 * 5: Teraco JHB (Gauteng, Limpopo, Mpumalanga, North West, Free State)
 * 6: Teraco CPT (Western Cape, Eastern Cape, Northern Cape)
 * 7: Teraco KZN (KwaZulu-Natal)
 * @param {string} provinceName - The name of the province.
 * @returns {number} The corresponding Location ID.
 */
export const mapProvinceToLocationId = (provinceName) => {
  if (!provinceName) return 5; // Default to JHB if province is missing

  const province = provinceName.toLowerCase();
  if (province.includes('kwazulu-natal') || province.includes('kzn') || province.includes('durban')) {
    return 7; // Teraco KZN
  } else if (
    province.includes('western cape') ||
    province.includes('eastern cape') ||
    province.includes('northern cape') ||
    province.includes('cape town')
  ) {
    return 6; // Teraco CPT
  } else {
    // Gauteng, Limpopo, Mpumalanga, North West, Free State
    return 5; // Teraco JHB (Default)
  }
};

/**
 * Submit signup form data to the API
 * @param {Object} formData - The complete form data object
 * @returns {Promise<Object>} - The API response
 */
export const submitSignupForm = async (formData) => {
  try {
    // Prepare the data for submission
    const payload = prepareSignupPayload(formData);
    
    console.log('Submitting signup form data:', payload);
    
    // Make the API request
    const response = await fetch(`${API_BASE_URL}/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Add any additional headers here if needed
      },
      body: JSON.stringify(payload),
      // Credentials include to handle cookies if needed
      credentials: 'include',
    });
    
    // Parse the response
    const data = await response.json();
    
    // Check if the request was successful
    if (!response.ok) {
      throw new Error(data.message || `Error: ${response.status}`);
    }
    
    return {
      success: true,
      data,
      message: data.message || 'Signup successful'
    };
  } catch (error) {
    console.error('Error submitting signup form:', error);
    return {
      success: false,
      error: error.message || 'An unknown error occurred',
      message: 'Failed to submit signup form'
    };
  }
};

/**
 * Prepare the signup payload from the form data
 * @param {Object} formData - The raw form data from the signup form
 * @returns {Object} - The formatted payload for the API
 */
export const prepareSignupPayload = (formData) => {
  // Extract package data
  const packageDetails = formData.packageData || {};
  
  // Calculate locationId based on province name
  const locationId = mapProvinceToLocationId(formData.provinceName);
  
  // Format the payload according to the expected API structure
  return {
    // Required fields
    referenceNumber: formData.referenceNumber || `WN-ORDER-${Math.floor(Math.random() * 1000000)}`,
    locationId: locationId, // Include locationId in the payload
    
    // Customer information
    customer: {
      title: formData.title || '',
      firstName: formData.firstName || '',
      surname: formData.surname || '',
      email: formData.email || '',
      cellphone: formData.cellphone || '',
      idNumber: formData.idNumber || '',
      existingCustomer: formData.existingCustomer === 'yes',
      promoCode: formData.promoCode || null,
      referralCode: formData.referralCode || null,
    },
    
    // Installation details
    installation: {
      routerOption: formData.routerOption || 'freeRouter',
      installationType: formData.installationStatus || '', // Installation type (migration, new installation, etc.)
      installationStatus: formData.installationStatus || '',
      installationDate: formData.installationDate ? formData.installationDate.toISOString() : null,
      address: {
        streetNumber: formData.streetNumber || '',
        streetName: formData.streetName || '',
        suburb: formData.suburb || '',
        city: formData.city || '',
        postalCode: formData.postalCode || '',
        provinceName: formData.installationAddress?.provinceName || 'Gauteng', // Default to Gauteng if not provided
        complex: formData.complex || null,
        unitNumber: formData.unitNumber || null,
        propertyType: formData.propertyType || '', // House, complex, etc.
        propertyOwnership: formData.propertyOwnership || '', // Owned or rented
        isOwned: formData.propertyOwnership === 'owned', // Boolean flag for owned property
        isRented: formData.propertyOwnership === 'rented', // Boolean flag for rented property
        coordinates: formData.latitude && formData.longitude
          ? { lat: formData.latitude, lng: formData.longitude }
          : formData.installationAddress?.placeDetails?.geometry?.location ?? null, // Use coordinates from form data or fallback to installationAddress
      },
    },
    
    // Landlord details (separate section)
    landlord: formData.propertyOwnership === 'rented' ? {
      name: formData.landlordName || '',
      contactNumber: formData.landlordContact || '',
      email: formData.landlordEmail || '',
    } : null,
    
    // Payment details
    payment: {
      accountNumber: formData.accountNumber || '',
      accountHolderName: formData.accountHolderName || '',
      bank: formData.bank || '',
      // branchName field removed as per API requirements
      branchCode: formData.branchCode || '',
      accountType: formData.accountType || '',
      authorisedDebitOrder: formData.authoriseDebitOrder || false,
      agreedToTerms: formData.agreeToTerms || false,
      signature: formData.signature || null,
    },
    
    // Package details
    package: {
      id: packageDetails.ID || packageDetails.id || 0, // Include the package ID
      name: packageDetails.Title || packageDetails.Name || '',
      speed: {
        download: packageDetails['Download speed (kbps)'] || 0,
        upload: packageDetails['Upload speed (kbps)'] || 0,
      },
      price: packageDetails.Price || 0,
      supplier: packageDetails.Supplier || '',
      providerName: formData.providerName || '',
    },
    
    // Metadata
    metadata: {
      submittedAt: new Date().toISOString(),
      source: window.location.href,
      userAgent: navigator.userAgent,
      clientIP: formData.clientIP || '127.0.0.1', // Default to localhost if not provided
    }
  };
};